import { type MouseEvent } from 'react';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { trackEcommerce, trackEvent, trackingConstants, eventNameConstants } from 'dibs-tracking';

import {
    AUTO_PROMPT_REGISTER,
    ALT_REGISTRATION_CONSUMER_FLOW,
    ALT_REGISTRATION_CONSUMER_FLOW_V2,
    DEFAULT_FLOW,
    PAGEVIEW_LIMIT_FLOW,
} from '../authentication/authFlowConstants.ts';
import {
    AUTO_PROMPT_REGISTER_V2,
    LOGIN,
    LOGIN_V2,
    REGISTER,
    REGISTER_V2,
    SOCIAL_REGISTER_APPLE_FAVORITES_V2,
    SOCIAL_REGISTER_FACEBOOK_FAVORITES_V2,
    SOCIAL_REGISTER_GOOGLE_FAVORITES_V2,
} from '../postAuth/constants.ts';

const PINTEREST_ONBOARDING_AB_TEST_NAME = 'Registration_UserPrefernceSurvey_July2026';

const PINTEREST_ONBOARDING_ELIGIBLE_AUTH_FLOWS = [
    DEFAULT_FLOW,
    PAGEVIEW_LIMIT_FLOW,
    ALT_REGISTRATION_CONSUMER_FLOW_V2,
] as const;

const V2_FLOW_ACTIONS = [
    AUTO_PROMPT_REGISTER_V2,
    LOGIN_V2,
    REGISTER_V2,
    SOCIAL_REGISTER_FACEBOOK_FAVORITES_V2,
    SOCIAL_REGISTER_GOOGLE_FAVORITES_V2,
    SOCIAL_REGISTER_APPLE_FAVORITES_V2,
] as const;

const LEGACY_TO_V2_POST_AUTH_FLOW: Record<string, string> = {
    [AUTO_PROMPT_REGISTER]: AUTO_PROMPT_REGISTER_V2,
    [LOGIN]: LOGIN_V2,
    [REGISTER]: REGISTER_V2,
};

export const ONBOARDING_FAVORITES_TRACKING_LIST = 'User Preference Survey';

export const ONBOARDING_FAVORITES_STEP_INTERACTION = {
    SAVE_AND_CONTINUE: 'Save and Continue clicked',
    SKIP_FOR_NOW: 'Skip for now clicked',
} as const;

const isPinterestOnboardingVariant = (): boolean =>
    getAbTestV2(PINTEREST_ONBOARDING_AB_TEST_NAME)?.variant === 'variant';

const isPinterestOnboardingEligibleAuthFlow = (authFlow: string | null): boolean =>
    authFlow !== null &&
    (PINTEREST_ONBOARDING_ELIGIBLE_AUTH_FLOWS as readonly string[]).includes(authFlow);

export const resolvePostAuthFlow = (postAuthFlow: string, authFlow: string | null): string => {
    const postAuthFlowUpperCase = postAuthFlow.toUpperCase();
    if (isPinterestOnboardingVariant() && isPinterestOnboardingEligibleAuthFlow(authFlow)) {
        return LEGACY_TO_V2_POST_AUTH_FLOW[postAuthFlowUpperCase] ?? postAuthFlow;
    }
    return postAuthFlow;
};

export const isAutoPromptRegisterFlowAction = (flowAction: string): boolean =>
    flowAction === AUTO_PROMPT_REGISTER || flowAction === AUTO_PROMPT_REGISTER_V2;

export const isLoginFlowAction = (flowAction: string): boolean =>
    flowAction === LOGIN || flowAction === LOGIN_V2;

export const isRegisterFlowAction = (flowAction: string): boolean =>
    flowAction === REGISTER || flowAction === REGISTER_V2;

export const isAltRegistrationConsumerFlowAction = (flowAction: string): boolean =>
    flowAction === ALT_REGISTRATION_CONSUMER_FLOW ||
    flowAction === ALT_REGISTRATION_CONSUMER_FLOW_V2;

export const isV2FlowAction = (flowAction: string): boolean =>
    (V2_FLOW_ACTIONS as readonly string[]).includes(flowAction);

export const shouldUseOnboardingFavorites = (authFlow: string): boolean =>
    authFlow === ALT_REGISTRATION_CONSUMER_FLOW_V2 ||
    (isPinterestOnboardingVariant() &&
        (authFlow === DEFAULT_FLOW || authFlow === PAGEVIEW_LIMIT_FLOW));

export const trackPinterestOnboardingTest = (): void => {
    trackAbTestV2Variant(PINTEREST_ONBOARDING_AB_TEST_NAME);
};

export const trackOnboardingFavoriteItemImpression = (
    products: Array<{ id: string; position: number }>
): void => {
    if (!products.length) {
        return;
    }

    trackEcommerce({
        type: trackingConstants.ECOM_PRODUCT_IMPRESSION,
        eventName: eventNameConstants.EVENT_VIEW_ITEM_LIST,
        trigger: ONBOARDING_FAVORITES_TRACKING_LIST,
        products: products.map(product => ({
            ...product,
            list: ONBOARDING_FAVORITES_TRACKING_LIST,
        })),
    });
};

export const trackOnboardingFavoritesButtonClick = (
    stepInteractionName: string,
    event: MouseEvent | null = null
): void => {
    trackEvent(
        {
            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
            interaction_type: ONBOARDING_FAVORITES_TRACKING_LIST,
            step_interaction_name: stepInteractionName,
            trigger: ONBOARDING_FAVORITES_TRACKING_LIST,
            isInteractiveEvent: true,
        },
        event
    );
};
